import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useStripe } from '../contexts/StripeContext';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import ImageUpload from './ImageUpload';
import AutocompleteInput from './AutocompleteInput';
import EnhancedDateTimePicker from './EnhancedDateTimePicker';
import { searchCities, getPopularCities } from '../utils/citiesData';

function CreateWorkshop() {
  const { currentUser } = useAuth();
  const { isOnboarded, isLoading: stripeLoading, startInstructorOnboarding } = useStripe();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [uploadError, setUploadError] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);

  const [workshop, setWorkshop] = useState({
    title: '',
    shortDescription: '',
    description: '',
    startDate: '',
    endDate: '',
    location: '',
    maxParticipants: 12,
    price: '',
    packages: [{ name: '', price: '', description: '' }],
    included: [''],
    itinerary: [{ day: 1, title: '', description: '', activities: [''] }],
    gearList: [{ category: 'Camera Equipment', items: [''] }],
    cancellationPolicy: '',
    imageUrl: '',
    imagePath: '',
    status: 'draft'
  });

  // Redirect to Stripe onboarding if not onboarded
  useEffect(() => {
    if (!stripeLoading && !isOnboarded) {
      console.log('Instructor not onboarded, showing onboarding blocker...');
    }
  }, [isOnboarded, stripeLoading]);

  // Initialize location suggestions
  useEffect(() => {
    setLocationSuggestions(getPopularCities());
  }, []);

  // Auto-save to localStorage
  useEffect(() => {
    const savedWorkshop = localStorage.getItem('draft_workshop');
    if (savedWorkshop) {
      const parsed = JSON.parse(savedWorkshop);
      if (!parsed.packages) {
        parsed.packages = [{ name: '', price: '', description: '' }];
      }
      setWorkshop(parsed);
    }
  }, []);

  // Auto-save with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      localStorage.setItem('draft_workshop', JSON.stringify(workshop));
      console.log('Auto-saved workshop draft');
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [workshop]);

  const steps = [
    { 
      id: 1, 
      title: 'Basics', 
      description: 'Workshop title, dates, and location'
    },
    { 
      id: 2, 
      title: 'Details', 
      description: 'Description and what\'s included'
    },
    { 
      id: 3, 
      title: 'Itinerary', 
      description: 'Day-by-day schedule'
    },
    { 
      id: 4, 
      title: 'Gear & Policies', 
      description: 'Equipment and cancellation policy'
    },
    { 
      id: 5, 
      title: 'Pricing & Publish', 
      description: 'Set prices and publish your workshop'
    }
  ];

  const handleInputChange = (field, value) => {
    setWorkshop(prev => ({ ...prev, [field]: value }));
    
    // Update location suggestions when typing
    if (field === 'location') {
      if (value.length > 0) {
        const suggestions = searchCities(value, 8);
        setLocationSuggestions(suggestions);
      } else {
        setLocationSuggestions(getPopularCities());
      }
    }
  };

  const handleLocationSelect = (suggestion) => {
    const locationStr = typeof suggestion === 'string' ? suggestion : suggestion.displayText || suggestion.city;
    handleInputChange('location', locationStr);
    setLocationSuggestions(getPopularCities()); // Reset to popular cities
  };

  const handleArrayChange = (field, index, value) => {
    setWorkshop(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayItem = (field, defaultValue = '') => {
    setWorkshop(prev => ({
      ...prev,
      [field]: [...prev[field], defaultValue]
    }));
  };

  const removeArrayItem = (field, index) => {
    setWorkshop(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const addItineraryDay = () => {
    setWorkshop(prev => ({
      ...prev,
      itinerary: [...prev.itinerary, { 
        day: prev.itinerary.length + 1, 
        title: '', 
        description: '', 
        activities: [''] 
      }]
    }));
  };

  const handleItineraryChange = (index, field, value) => {
    setWorkshop(prev => ({
      ...prev,
      itinerary: prev.itinerary.map((day, i) => 
        i === index ? { ...day, [field]: value } : day
      )
    }));
  };

  const addItineraryActivity = (dayIndex) => {
    setWorkshop(prev => ({
      ...prev,
      itinerary: prev.itinerary.map((day, i) => 
        i === dayIndex ? { ...day, activities: [...day.activities, ''] } : day
      )
    }));
  };

  const handleActivityChange = (dayIndex, activityIndex, value) => {
    setWorkshop(prev => ({
      ...prev,
      itinerary: prev.itinerary.map((day, i) => 
        i === dayIndex ? {
          ...day,
          activities: day.activities.map((activity, j) => 
            j === activityIndex ? value : activity
          )
        } : day
      )
    }));
  };

  const addGearCategory = () => {
    setWorkshop(prev => ({
      ...prev,
      gearList: [...prev.gearList, { category: '', items: [''] }]
    }));
  };

  const handleGearCategoryChange = (index, field, value) => {
    setWorkshop(prev => ({
      ...prev,
      gearList: prev.gearList.map((category, i) => 
        i === index ? { ...category, [field]: value } : category
      )
    }));
  };

  const addGearItem = (categoryIndex) => {
    setWorkshop(prev => ({
      ...prev,
      gearList: prev.gearList.map((category, i) => 
        i === categoryIndex ? { ...category, items: [...category.items, ''] } : category
      )
    }));
  };

  const handleGearItemChange = (categoryIndex, itemIndex, value) => {
    setWorkshop(prev => ({
      ...prev,
      gearList: prev.gearList.map((category, i) => 
        i === categoryIndex ? {
          ...category,
          items: category.items.map((item, j) => 
            j === itemIndex ? value : item
          )
        } : category
      )
    }));
  };

  const addPackage = () => {
    setWorkshop(prev => ({
      ...prev,
      packages: [...prev.packages, { name: '', price: '', description: '' }]
    }));
  };

  const removePackage = (index) => {
    setWorkshop(prev => ({
      ...prev,
      packages: prev.packages.filter((_, i) => i !== index)
    }));
  };

  const handlePackageChange = (index, field, value) => {
    setWorkshop(prev => ({
      ...prev,
      packages: prev.packages.map((pkg, i) => 
        i === index ? { ...pkg, [field]: value } : pkg
      )
    }));
  };

  const handleImageUploadComplete = (uploadResult) => {
    setWorkshop(prev => ({
      ...prev,
      imageUrl: uploadResult.url,
      imagePath: uploadResult.path
    }));
    setUploadError('');
  };

  const handleImageUploadError = (error) => {
    setUploadError(error);
  };

  const isStepValid = (step) => {
    switch (step) {
      case 1:
        return workshop.title.trim() && workshop.startDate && workshop.endDate && workshop.location.trim();
      case 2:
        return workshop.description.trim() && workshop.included.some(item => item.trim());
      case 3:
        return workshop.itinerary.every(day => day.title.trim() && day.description.trim());
      case 4:
        return workshop.cancellationPolicy.trim();
      case 5:
        return workshop.packages.some(pkg => pkg.name.trim() && pkg.price);
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (isStepValid(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleStepClick = (stepNumber) => {
    setCurrentStep(stepNumber);
  };

  const handleSubmit = async (status = 'draft') => {
    if (!currentUser) {
      setError('You must be logged in to create a workshop');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Validate required fields
      if (!workshop.title || !workshop.description || !workshop.startDate || !workshop.endDate || !workshop.location) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      // Validate dates
      const startDate = new Date(workshop.startDate);
      const endDate = new Date(workshop.endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate < today) {
        setError('Start date cannot be in the past');
        setLoading(false);
        return;
      }

      if (endDate <= startDate) {
        setError('End date must be after start date');
        setLoading(false);
        return;
      }

      // Prepare workshop data
      const workshopData = {
        ...workshop,
        status,
        instructorId: currentUser.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        currentParticipants: 0,
        googleCalendarEventId: null,
        calendarEventCreated: false,
        attendeeVisibility: true,
        calendarCreationError: null
      };

      // Create workshop in Firestore
      const docRef = await addDoc(collection(db, 'sessions_workshops'), workshopData);
      
      // Clear draft from localStorage
      localStorage.removeItem('draft_workshop');

      // If publishing (not draft), try to create calendar event
      if (status === 'published') {
        try {
          const response = await fetch('https://klorah-fast-server.onrender.com/api/sessions/create-calendar-event', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-User-ID': currentUser.uid
            },
            body: JSON.stringify({
              workshop_id: docRef.id
            })
          });

          if (response.ok) {
            const calendarData = await response.json();
            console.log('Calendar event created successfully:', calendarData);
          } else {
            console.warn('Failed to create calendar event, but workshop was created');
          }
        } catch (calendarError) {
          console.warn('Calendar event creation failed, but workshop was created:', calendarError);
        }
      }

      // Navigate to workshop management
      navigate(`/workshop/${docRef.id}`);
      
    } catch (error) {
      console.error('Error creating workshop:', error);
      setError('Failed to create workshop. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartOnboarding = async () => {
    try {
      await startInstructorOnboarding();
    } catch (error) {
      console.error('Error starting onboarding:', error);
      // Don't navigate away, let the user try again
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Workshop Basics</h2>
              <p className="text-gray-600">Start with the essential information</p>
            </div>

            <div className="bg-white p-8 space-y-6 border border-gray-300 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Workshop Title *
                </label>
                <input
                  type="text"
                  value={workshop.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Advanced Landscape Photography Workshop"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <EnhancedDateTimePicker
                  value={workshop.startDate}
                  onChange={(value) => handleInputChange('startDate', value)}
                  label="Start Date & Time"
                  required
                  placeholder="Select start date and time"
                />
                <EnhancedDateTimePicker
                  value={workshop.endDate}
                  onChange={(value) => handleInputChange('endDate', value)}
                  label="End Date & Time"
                  required
                  minDate={workshop.startDate}
                  placeholder="Select end date and time"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location *
                </label>
                <AutocompleteInput
                  value={workshop.location}
                  onChange={(value) => handleInputChange('location', value)}
                  onSelect={handleLocationSelect}
                  suggestions={locationSuggestions}
                  placeholder="Enter city or location"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Participants
                </label>
                <input
                  type="number"
                  value={workshop.maxParticipants}
                  onChange={(e) => handleInputChange('maxParticipants', parseInt(e.target.value) || 12)}
                  min="1"
                  max="50"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Workshop Details</h2>
              <p className="text-gray-600">Describe your workshop and what's included</p>
            </div>

            <div className="bg-white p-8 space-y-6 border border-gray-300 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Short Description
                </label>
                <input
                  type="text"
                  value={workshop.shortDescription}
                  onChange={(e) => handleInputChange('shortDescription', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief overview of the workshop"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Description *
                </label>
                <textarea
                  value={workshop.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Detailed description of what students will learn and experience..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What's Included
                </label>
                {workshop.included.map((item, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => handleArrayChange('included', index, e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Professional instruction, meals, transportation"
                    />
                    {workshop.included.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeArrayItem('included', index)}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('included', '')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Add Item
                </button>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Workshop Itinerary</h2>
              <p className="text-gray-600">Plan your day-by-day schedule</p>
            </div>

            <div className="bg-white p-8 space-y-6 border border-gray-300 rounded-lg">
              {workshop.itinerary.map((day, dayIndex) => (
                <div key={dayIndex} className="border border-gray-300 rounded-lg p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Day {day.day}</h3>
                    {workshop.itinerary.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          setWorkshop(prev => ({
                            ...prev,
                            itinerary: prev.itinerary.filter((_, i) => i !== dayIndex)
                          }));
                        }}
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                      >
                        Remove Day
                      </button>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Day Title *
                      </label>
                      <input
                        type="text"
                        value={day.title}
                        onChange={(e) => handleItineraryChange(dayIndex, 'title', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Introduction and Camera Basics"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Day Description *
                      </label>
                      <textarea
                        value={day.description}
                        onChange={(e) => handleItineraryChange(dayIndex, 'description', e.target.value)}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Describe what will happen on this day..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Activities
                      </label>
                      {day.activities.map((activity, activityIndex) => (
                        <div key={activityIndex} className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={activity}
                            onChange={(e) => handleActivityChange(dayIndex, activityIndex, e.target.value)}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g., Morning photography session"
                          />
                          {day.activities.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                setWorkshop(prev => ({
                                  ...prev,
                                  itinerary: prev.itinerary.map((d, i) => 
                                    i === dayIndex ? {
                                      ...d,
                                      activities: d.activities.filter((_, j) => j !== activityIndex)
                                    } : d
                                  )
                                }));
                              }}
                              className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addItineraryActivity(dayIndex)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Add Activity
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addItineraryDay}
                className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700"
              >
                Add Day
              </button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Gear & Policies</h2>
              <p className="text-gray-600">Equipment requirements and cancellation policy</p>
            </div>

            <div className="bg-white p-8 space-y-6 border border-gray-300 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cancellation Policy *
                </label>
                <textarea
                  value={workshop.cancellationPolicy}
                  onChange={(e) => handleInputChange('cancellationPolicy', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Explain your cancellation and refund policy..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Required Gear
                </label>
                {workshop.gearList.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="border border-gray-300 rounded-lg p-4 mb-4">
                    <div className="flex justify-between items-center mb-3">
                      <input
                        type="text"
                        value={category.category}
                        onChange={(e) => handleGearCategoryChange(categoryIndex, 'category', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Category name"
                      />
                      {workshop.gearList.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            setWorkshop(prev => ({
                              ...prev,
                              gearList: prev.gearList.filter((_, i) => i !== categoryIndex)
                            }));
                          }}
                          className="ml-2 px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    {category.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => handleGearItemChange(categoryIndex, itemIndex, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Gear item"
                        />
                        {category.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              setWorkshop(prev => ({
                                ...prev,
                                gearList: prev.gearList.map((cat, i) => 
                                  i === categoryIndex ? {
                                    ...cat,
                                    items: cat.items.filter((_, j) => j !== itemIndex)
                                  } : cat
                                )
                              }));
                            }}
                            className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() => addGearItem(categoryIndex)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Add Item
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addGearCategory}
                  className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700"
                >
                  Add Category
                </button>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Pricing & Publish</h2>
              <p className="text-gray-600">Set your prices and publish your workshop</p>
            </div>

            <div className="bg-white p-8 space-y-6 border border-gray-300 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Workshop Image
                </label>
                <ImageUpload
                  onUploadComplete={handleImageUploadComplete}
                  onUploadError={handleImageUploadError}
                  currentImage={workshop.imageUrl}
                  uploadPath={`workshops/${currentUser.uid}`}
                />
                {uploadError && (
                  <p className="text-red-600 text-sm mt-2">{uploadError}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pricing Packages
                </label>
                {workshop.packages.map((pkg, index) => (
                  <div key={index} className="border border-gray-300 rounded-lg p-4 mb-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-lg font-semibold text-gray-900">Package {index + 1}</h4>
                      {workshop.packages.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removePackage(index)}
                          className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Package Name *
                        </label>
                        <input
                          type="text"
                          value={pkg.name}
                          onChange={(e) => handlePackageChange(index, 'name', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., Standard Package"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Price *
                        </label>
                        <input
                          type="number"
                          value={pkg.price}
                          onChange={(e) => handlePackageChange(index, 'price', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0"
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description
                        </label>
                        <textarea
                          value={pkg.description}
                          onChange={(e) => handlePackageChange(index, 'description', e.target.value)}
                          rows={3}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="What's included in this package..."
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addPackage}
                  className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700"
                >
                  Add Package
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (stripeLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-900">Loading...</div>
      </div>
    );
  }

  // Show blocker wall if not onboarded
  if (!isOnboarded) {
    return (
      <div className="min-h-screen bg-klorah-dark flex items-center justify-center px-6">
        <div className="max-w-lg w-full">
          <div className="glass-panel p-8 text-center rounded-xl border border-klorah-border">
            <div className="w-20 h-20 bg-gradient-to-br from-klorah-primary to-klorah-secondary mx-auto mb-8 flex items-center justify-center rounded-full">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            
            <h1 className="text-3xl font-bold text-white mb-4 tracking-wider">
              PAYMENT SETUP REQUIRED
            </h1>
            
            <p className="text-klorah-text mb-8 text-lg leading-relaxed">
              Complete your Stripe onboarding to create workshops and receive payments from students. This secure process takes just a few minutes.
            </p>
            
            <div className="space-y-6">
              <div className="glass-panel-subtle p-6 text-left space-y-3 rounded-xl">
                <h3 className="text-white font-semibold mb-3 tracking-wider">WHAT YOU'LL NEED:</h3>
                <ul className="space-y-2 text-klorah-text">
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-klorah-primary rounded-full mr-3"></div>
                    Government-issued ID
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-klorah-primary rounded-full mr-3"></div>
                    Bank account information
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-klorah-primary rounded-full mr-3"></div>
                    Tax identification number
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-klorah-primary rounded-full mr-3"></div>
                    Business information (if applicable)
                  </li>
                </ul>
              </div>
              
              <div className="space-y-4">
                <button
                  onClick={handleStartOnboarding}
                  className="w-full klorah-button text-lg py-4"
                >
                  START PAYMENT SETUP
                </button>
                
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full px-6 py-3 bg-klorah-card border border-klorah-border text-klorah-text hover:text-white hover:bg-klorah-border rounded-xl transition-all tracking-wider"
                >
                  BACK TO DASHBOARD
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-klorah-dark">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <button
                  onClick={() => handleStepClick(step.id)}
                  className={`flex items-center justify-center w-12 h-12 rounded-full border-2 text-sm font-medium transition-all ${
                    currentStep === step.id
                      ? 'bg-klorah-primary border-klorah-primary text-black'
                      : step.id < currentStep
                      ? 'bg-emerald-500 border-emerald-500 text-white'
                      : 'bg-klorah-card border-klorah-border text-klorah-text-light'
                  }`}
                >
                  {step.id < currentStep ? '✓' : step.id}
                </button>
                {index < steps.length - 1 && (
                  <div className={`w-16 h-1 mx-2 transition-colors ${
                    step.id < currentStep ? 'bg-emerald-500' : 'bg-klorah-border'
                  }`} />
                )}
              </div>
            ))}
          </div>
          
          <div className="mt-4 text-center">
            <h1 className="text-2xl font-bold text-white">
              {steps[currentStep - 1].title}
            </h1>
            <p className="text-klorah-text">{steps[currentStep - 1].description}</p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl">
            {error}
          </div>
        )}

        {/* Step Content */}
        <form onSubmit={(e) => e.preventDefault()}>
          {renderStepContent()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className="px-6 py-3 bg-klorah-card border border-klorah-border text-klorah-text font-medium rounded-xl hover:bg-klorah-border hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Previous
            </button>

            <div className="flex space-x-4">
              {currentStep < steps.length ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!isStepValid(currentStep)}
                  className="klorah-button disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => handleSubmit('draft')}
                    disabled={loading}
                    className="px-6 py-3 bg-klorah-card border border-klorah-border text-klorah-text font-medium rounded-xl hover:bg-klorah-border hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {loading ? 'Saving...' : 'Save as Draft'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSubmit('published')}
                    disabled={loading || !isStepValid(currentStep)}
                    className="klorah-button disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Publishing...' : 'Publish Workshop'}
                  </button>
                </>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateWorkshop; 